import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import DataTable from '../components/DataTable';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import CustomFieldsSection from '../components/CustomFieldsSection';
import { Button, Modal, EmptyState, Pager, ListPageHeader, ListPageStats, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP', 'CHF', 'SAR', 'AED'];

const ACCOUNT_TYPES = [
  { value: 'demand',  label: 'Vadesiz Hesap',  icon: '🏦', color: '#0284c7' },
  { value: 'term',    label: 'Vadeli Hesap',   icon: '📅', color: '#7c3aed' },
  { value: 'current', label: 'Cari Hesap',     icon: '💼', color: '#16a34a' },
  { value: 'savings', label: 'Birikim Hesabi', icon: '🐷', color: '#d97706' },
  { value: 'other',   label: 'Diğer',          icon: '📋', color: '#64748b' },
];

const emptyForm = {
  account_name: '', bank_name: '', branch_name: '', account_number: '', iban: '',
  currency_code: 'TRY', account_type: 'demand',
  term_start_date: '', term_end_date: '', interest_rate: '',
  bank_rep_name: '', bank_rep_phone: '', notes: '', is_active: true, branch_id: '',
};

const TX_DISPLAY = {
  opening:       { label: 'Açılış Fişi',       sign:  1, color: '#7c3aed' },
  credit:        { label: 'Alacak Dekontu',    sign:  1, color: '#16a34a' },
  debit:         { label: 'Borç Dekontu',      sign: -1, color: '#dc2626' },
  virman_in:     { label: 'Virman Girişi',     sign:  1, color: '#0284c7' },
  virman_out:    { label: 'Virman Çıkışı',     sign: -1, color: '#d97706' },
  check_deposit: { label: 'Çek/Senet (Bekliyor)', sign: 0, color: '#64748b', informational: true },
};

function fmt(n, cur = 'TRY') {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + ' ' + cur;
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}
function accTypeInfo(type) {
  return ACCOUNT_TYPES.find(t => t.value === type) || ACCOUNT_TYPES[0];
}
function txDisplay(type) {
  return TX_DISPLAY[type] || { label: type, sign: 1, color: '#888' };
}

// ─── BANKA YÖNETİM PANELİ ────────────────────────────────────
function BankManagerPanel({ onClose }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newName, setNewName] = useState('');
  const [newSwift, setNewSwift] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', swift_code: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bank-accounts/bank-defs');
      if (res.data.success) setBanks(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await api.post('/bank-accounts/bank-defs', { name: newName.trim(), swift_code: newSwift.trim() || null });
      setNewName(''); setNewSwift(''); setAddMode(false); load();
    } catch (err) { alert(err.response?.data?.message || 'Eklenemedi'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    if (!editForm.name.trim()) return;
    setSaving(true);
    try {
      await api.put(`/bank-accounts/bank-defs/${id}`, editForm);
      setEditingId(null); load();
    } catch (err) { alert(err.response?.data?.message || 'Güncellenemedi'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" bankasini silmek istiyor musunuz?`)) return;
    try { await api.delete(`/bank-accounts/bank-defs/${id}`); load(); }
    catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  return (
    <Modal icon={TAB_ICONS.bank} title="Banka Tanımı Yonetimi" subtitle={<span>{banks.length} banka tanımlı</span>} size="md" onClose={onClose}>
        <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 10 }}>
          {loading ? <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 16, fontSize: '0.83rem' }}>Yükleniyor...</div>
            : banks.length === 0 ? <EmptyState title="Banka yok" description="Henuz banka eklenmemis" compact />
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                <thead><tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                  <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>Banka Adi</th>
                  <th style={{ padding: '7px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600 }}>SWIFT</th>
                  <th style={{ width: 90 }}></th>
                </tr></thead>
                <tbody>
                  {banks.map((bank, i) => (
                    <tr key={bank.id} style={{ borderBottom: i < banks.length - 1 ? '1px solid var(--border-secondary)' : 'none' }}>
                      {editingId === bank.id ? (
                        <>
                          <td style={{ padding: '5px 8px' }}>
                            <input className="ui-input" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                              autoFocus onKeyDown={e => { if (e.key === 'Enter') handleUpdate(bank.id); if (e.key === 'Escape') setEditingId(null); }}
                              style={{ width: '100%' }} />
                          </td>
                          <td style={{ padding: '5px 8px' }}>
                            <input className="ui-input" value={editForm.swift_code || ''} onChange={e => setEditForm(p => ({ ...p, swift_code: e.target.value }))}
                              style={{ width: '100%' }} />
                          </td>
                          <td style={{ padding: '5px 8px' }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <Button size="sm" onClick={() => handleUpdate(bank.id)} loading={saving}>Kaydet</Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>X</Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: '9px 12px', fontWeight: 500, color: 'var(--text-primary)' }}>{bank.name}</td>
                          <td style={{ padding: '9px 8px', color: 'var(--text-tertiary)', fontFamily: 'monospace', fontSize: '0.78rem' }}>{bank.swift_code || '—'}</td>
                          <td style={{ padding: '9px 8px' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              <Button variant="ghost" size="sm" onClick={() => { setEditingId(bank.id); setEditForm({ name: bank.name, swift_code: bank.swift_code || '' }); }}>Duzenle</Button>
                              <Button variant="danger" size="sm" onClick={() => handleDelete(bank.id, bank.name)}>Sil</Button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
        {addMode ? (
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input className="ui-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Banka adi *" autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAddMode(false); setNewName(''); setNewSwift(''); } }}
              style={{ flex: 1 }} />
            <input className="ui-input" value={newSwift} onChange={e => setNewSwift(e.target.value)} placeholder="SWIFT"
              style={{ width: 110 }} />
            <Button size="sm" onClick={handleAdd} disabled={saving || !newName.trim()}>Ekle</Button>
            <Button variant="ghost" size="sm" onClick={() => { setAddMode(false); setNewName(''); setNewSwift(''); }}>İptal</Button>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setAddMode(true)} style={{ width: '100%', marginTop: 4 }}>+ Yeni Banka Ekle</Button>
        )}
    </Modal>
  );
}

// ─── ANA SAYFA ────────────────────────────────────────────────
export default function BankAccountsPage({ autoOpenNew = false, onSaved = null, onNavigate = null }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  const [bankDefs, setBankDefs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showBankManager, setShowBankManager] = useState(false);

  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ bank_name: '', account_type: '', currency_code: '', is_active: '' });

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  const fetchTxs = useCallback(async (id) => {
    if (!id) return;
    setTxLoading(true);
    try {
      const res = await api.get('/bank-accounts/' + id + '/transactions', { params: { limit: 300 } });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch {} finally { setTxLoading(false); }
  }, []);

  const fetchBankDefs = useCallback(async () => {
    try {
      const res = await api.get('/bank-accounts/bank-defs');
      if (res.data.success) setBankDefs(res.data.data.filter(b => b.is_active));
    } catch {}
  }, []);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/bank-accounts', { params });
      if (res.data.success) setAccounts(res.data.data.items || []);
    } catch {} finally { setLoading(false); }
  }, [search, filters]);

  useEffect(() => { fetchBankDefs(); }, [fetchBankDefs]);
  useEffect(() => {
    api.get('/branches', { params: { is_active: 'yes' } })
      .then(r => { if (r.data?.success) setBranches(r.data.data.items || []); })
      .catch(() => {});
  }, []);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => {
    if (autoOpenNew) {
      setSelected(null); setForm({...emptyForm}); setIsNew(true); setIsEditing(true);
      setActiveTab('general'); setMsg(null); setShowModal(true);
    }
  }, [autoOpenNew]);

  const openNew = () => {
    setSelected(null); setForm({ ...emptyForm });
    setIsNew(true); setIsEditing(true); setActiveTab('general'); setMsg(null);
    setShowModal(true);
  };

  const openDetail = async (acc) => {
    try {
      const res = await api.get('/bank-accounts/' + acc.id);
      if (res.data.success) {
        const d = res.data.data;
        if (d.term_start_date) d.term_start_date = d.term_start_date.substring(0, 10);
        if (d.term_end_date)   d.term_end_date   = d.term_end_date.substring(0, 10);
        setSelected(d); setForm(d);
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        setTransactions([]);
        setShowModal(true);
        fetchTxs(d.id);
      }
    } catch {}
  };

  const closeModal = () => {
    setShowModal(false); setSelected(null);
    setIsEditing(false); setIsNew(false); setMsg(null);
    setTransactions([]);
  };

  const refreshSelected = async (id) => {
    const r = await api.get('/bank-accounts/' + id);
    if (r.data.success) {
      const d = r.data.data;
      if (d.term_start_date) d.term_start_date = d.term_start_date.substring(0, 10);
      if (d.term_end_date)   d.term_end_date   = d.term_end_date.substring(0, 10);
      setSelected(d); setForm(d);
    }
  };

  // mode: 'close' (default) | 'new' (kaydet ve yeni aç) | 'copy' (kaydet ve kopyala)
  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.account_name || !form.bank_name) { setMsg({ type: 'error', text: 'Hesap adi ve banka adi zorunludur' }); return; }
    if (form.account_type === 'term' && (!form.term_start_date || !form.term_end_date)) { setMsg({ type: 'error', text: 'Vadeli hesap icin vade tarihleri zorunludur' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/bank-accounts', form);
        setIsNew(false); setIsEditing(false);
        fetchAccounts(); await refreshSelected(res.data.data.id);
        setMsg({ type: 'success', text: 'Hesap olusturuldu' });
        if (onSaved) onSaved(res.data.data);
      } else {
        await api.put('/bank-accounts/' + selected.id, form);
        setIsEditing(false); fetchAccounts(); await refreshSelected(selected.id);
        setMsg({ type: 'success', text: 'Kaydedildi' });
      }
      // Post-save mode handling
      if (mode === 'close') {
        setTimeout(() => closeModal(), 250);
      } else if (mode === 'new') {
        setTimeout(() => {
          setSelected(null); setForm({ ...emptyForm });
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      } else if (mode === 'copy') {
        setTimeout(() => {
          const copy = { ...form, id: undefined, account_name: form.account_name + ' (kopya)', account_number: '', iban: '' };
          setSelected(null); setForm(copy);
          setIsEditing(true); setIsNew(true); setActiveTab('general'); setMsg(null);
        }, 250);
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Hata olustu' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Bu hesabi silmek istediginize emin misiniz?')) return;
    try { await api.delete('/bank-accounts/' + selected.id); closeModal(); fetchAccounts(); }
    catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Silinemedi' }); }
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const isTermAccount = form.account_type === 'term';

  const filtered = accounts.filter(a =>
    !search || a.account_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.bank_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.iban?.toLowerCase().includes(search.toLowerCase()) ||
    a.account_number?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / LIMIT) || 1;
  const paged = filtered.slice((page - 1) * LIMIT, page * LIMIT);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Banka Hesapları"
        count={accounts.length}
        unit="hesap"
        search={search}
        onSearch={v => { setSearch(v); setPage(1); }}
        searchPlaceholder="Hesap adi, banka, IBAN ara..."
        onRefresh={fetchAccounts}
        refreshing={loading}
        actions={<>
          <Button variant="ghost" size="sm" onClick={() => setShowBankManager(true)}
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>}>
            Bankalar ({bankDefs.length})
          </Button>
          <Button size="sm" onClick={openNew}>+ Yeni Hesap</Button>
        </>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={v => { setFilters(v); setPage(1); }}
        fields={[
          { key: 'bank_name', type: 'select', label: 'Banka', flex: '1 1 200px', minWidth: 180,
            options: [{ value: '', label: 'Tümü' }, ...bankDefs.map(b => ({ value: b.name, label: b.name }))] },
          { key: 'account_type', type: 'select', label: 'Hesap Türü',
            options: [{ value: '', label: 'Tümü' }, ...ACCOUNT_TYPES.map(t => ({ value: t.value, label: t.label }))] },
          { key: 'currency_code', type: 'select', label: 'Para Birimi',
            options: [{ value: '', label: 'Tümü' }, ...CURRENCIES.map(c => ({ value: c, label: c }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="bank-accounts-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Hesap bulunamadı"
          data={paged}
          rowKey="id"
          onRowClick={acc => openDetail(acc)}
          columns={[
            { id: 'account_name', header: 'Hesap Adi', accessor: 'account_name', sortable: true,
              cell: acc => <span style={{ fontWeight: 600 }}>{acc.account_name}</span>, width: 160 },
            { id: 'bank', header: 'Banka / Şube', accessor: a => `${a.bank_name||''} ${a.branch_name||''}`, sortable: true,
              cell: acc => <span className="stock-secondary">{acc.bank_name}{acc.branch_name && <span style={{ color: 'var(--text-tertiary)' }}> / {acc.branch_name}</span>}</span>, width: 180 },
            { id: 'type', header: 'Hesap Türü', accessor: 'account_type', sortable: true, width: 130,
              cell: acc => { const at = accTypeInfo(acc.account_type); return <span style={{ background: at.color + '18', color: at.color, borderRadius: 99, padding: '2px 9px', fontSize: '0.74rem', fontWeight: 600 }}>{at.icon} {at.label}</span>; } },
            { id: 'no', header: 'Hesap No / IBAN', accessor: a => `${a.account_number||''} ${a.iban||''}`, sortable: true, width: 200,
              cell: acc => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{acc.account_number && <div>{acc.account_number}</div>}{acc.iban && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.72rem' }}>{acc.iban}</div>}</span> },
            { id: 'rep', header: 'Mst. Temsilcisi', accessor: 'bank_rep_name', sortable: true, width: 160,
              cell: acc => <span className="stock-secondary">{acc.bank_rep_name ? (<div><div>{acc.bank_rep_name}</div>{acc.bank_rep_phone && <div style={{ color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>{acc.bank_rep_phone}</div>}</div>) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</span> },
            { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.current_balance)||0, sortable: true,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, fontSize: '0.88rem' }, width: 180,
              cell: acc => {
                const cp = parseFloat(acc.pending_check_amount) || 0;
                const np = parseFloat(acc.pending_note_amount) || 0;
                const totalPending = cp + np;
                return (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap: 2 }}>
                    <span style={{ color: parseFloat(acc.current_balance) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {fmt(acc.current_balance, acc.currency_code)}
                    </span>
                    {totalPending > 0 && (
                      <span title={`Bankada tahsilatı bekleyen: ${cp > 0 ? 'Çek ' + fmt(cp, acc.currency_code) : ''}${cp > 0 && np > 0 ? ' + ' : ''}${np > 0 ? 'Senet ' + fmt(np, acc.currency_code) : ''}`}
                            style={{ fontSize:'0.7rem', fontWeight: 600, color:'#7c3aed', padding:'1px 6px', borderRadius: 100,
                                     background:'rgba(124,58,237,0.10)', border:'1px solid rgba(124,58,237,0.25)' }}>
                        📜 +{fmt(totalPending, acc.currency_code)}
                      </span>
                    )}
                  </div>
                );
              } },
            { id: 'status', header: 'Durum', accessor: a => a.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 90,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: acc => <span className={'stock-badge ' + (acc.is_active ? 'active' : 'passive')}>{acc.is_active ? 'Aktif' : 'Pasif'}</span> },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: acc => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/bank-accounts/${acc.id}/audit`} /></span> },
          ]}
        />
      </div>

      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      {/* DETAY MODAL */}
      {showModal && (() => {
        const inEdit = isEditing;
        const inView = !isEditing && !isNew;
        const handleCancel = () => { if (isNew) closeModal(); else { setIsEditing(false); setForm(selected); setMsg(null); } };
        return (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Banka Hesabı' : 'Banka Hesabı'}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={handleCancel}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kopyala', icon: '⎘', onClick: () => handleSave('copy') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setIsEditing(true); setMsg(null); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
          bodyStyle={{ padding: 0 }}
        >
          {msg && (
            <div style={{
              margin: '12px 16px 0', padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          {/* Hero strip + KPI */}
          {!isNew && selected && (
            <div style={{ padding: '12px 16px 0' }}>
              <CardHero
                icon="🏦"
                code={form.account_name}
                title={[form.bank_name, form.branch_name].filter(Boolean).join(' / ')}
                subtitle={[
                  accTypeInfo(form.account_type).icon + ' ' + accTypeInfo(form.account_type).label,
                  form.currency_code,
                  selected.iban,
                ].filter(Boolean).join(' · ')}
                statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
                kpis={[
                  {
                    label: 'Bakiye',
                    value: fmt(selected.current_balance, selected.currency_code),
                    color: selected.current_balance >= 0 ? 'success' : 'danger',
                  },
                  selected.account_number && { label: 'Hesap No', value: selected.account_number, color: 'neutral' },
                  selected.account_type === 'term' && selected.term_end_date && {
                    label: 'Vade Bitişi', value: fmtDate(selected.term_end_date), color: 'warning',
                  },
                  selected.account_type === 'term' && selected.interest_rate && {
                    label: 'Faiz', value: '%' + parseFloat(selected.interest_rate).toFixed(2), color: 'success',
                  },
                  selected.bank_rep_name && { label: 'Temsilci', value: selected.bank_rep_name.substring(0, 20), color: 'info', sublabel: selected.bank_rep_phone },
                ].filter(Boolean)}
              />
            </div>
          )}

          <div style={{ padding: '0 16px' }}>
            <CardTabs
              items={[
                { key: 'general', label: 'Genel Bilgiler', icon: <TabIcon name="general"/> },
                ...(!isNew ? [{ key: 'hareketler', label: 'Hareketler', icon: <TabIcon name="activity"/>, badge: transactions.length || null }] : []),
                ...(!isNew ? [{ key: 'custom', label: 'Özel', icon: <TabIcon name="sparkles"/> }] : []),
                { key: 'notes', label: 'Notlar', icon: <TabIcon name="notes"/> },
              ]}
              active={activeTab}
              onChange={setActiveTab}
            />
          </div>

          {/* Eski .detail-tabs kaldırıldı — alttaki içerikleri tutmak için gizli wrapper */}
          <div className="detail-tabs" style={{ display: 'none' }}>
            {[
              { key: 'general', label: 'Genel Bilgiler', icon: 'general' },
              ...(!isNew ? [{ key: 'hareketler', label: 'Hareketler', icon: 'activity' }] : []),
              { key: 'notes', label: 'Notlar', icon: 'notes' },
            ].map(t => (
              <button key={t.key} className={'tab-btn' + (activeTab === t.key ? ' active' : '')} onClick={() => setActiveTab(t.key)}>
                <TabIcon name={t.icon}/><span>{t.label}</span>
              </button>
            ))}
          </div>

          <div className="detail-body" style={{ padding: 16 }}>
              {activeTab === 'general' && (
                <div className="form-grid">
                  <div className="form-row full">
                    <label>Hesap Türü</label>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {ACCOUNT_TYPES.map(at => (
                          <button key={at.value} onClick={() => f('account_type', at.value)} style={{
                            padding: '5px 12px', borderRadius: 99, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                            background: form.account_type === at.value ? at.color : at.color + '18',
                            color: form.account_type === at.value ? '#fff' : at.color,
                            border: `1px solid ${at.color}`,
                          }}>{at.icon} {at.label}</button>
                        ))}
                      </div>
                    ) : (
                      <div>{(() => { const at = accTypeInfo(form.account_type); return <span style={{ background: at.color + '18', color: at.color, borderRadius: 99, padding: '4px 12px', fontSize: '0.82rem', fontWeight: 600 }}>{at.icon} {at.label}</span>; })()}</div>
                    )}
                  </div>

                  <div className="form-row"><label>Hesap Adi *</label><input value={form.account_name || ''} onChange={e => f('account_name', e.target.value)} disabled={!isEditing} placeholder="Ornek: Ana TRY Hesabi" /></div>

                  <div className="form-row">
                    <label>Bağlı Şube</label>
                    <select value={form.branch_id || ''} onChange={e => f('branch_id', e.target.value || null)} disabled={!isEditing}>
                      <option value="">— Seçiniz —</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
                    </select>
                  </div>

                  <div className="form-row">
                    <label>Banka *</label>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select value={form.bank_name || ''} onChange={e => f('bank_name', e.target.value)} style={{ flex: 1 }}>
                          <option value="">Banka Secin</option>
                          {bankDefs.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                          {form.bank_name && !bankDefs.find(b => b.name === form.bank_name) && <option value={form.bank_name}>{form.bank_name}</option>}
                        </select>
                        <Button variant="ghost" size="sm" onClick={() => setShowBankManager(true)}>⚙ Bankalar</Button>
                      </div>
                    ) : <input value={form.bank_name || ''} disabled />}
                  </div>

                  <div className="form-row"><label>Şube</label><input value={form.branch_name || ''} onChange={e => f('branch_name', e.target.value)} disabled={!isEditing} placeholder="Şube adi veya kodu" /></div>
                  <div className="form-row">
                    <label>Para Birimi</label>
                    {isEditing ? <select value={form.currency_code || 'TRY'} onChange={e => f('currency_code', e.target.value)}>{CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
                      : <input value={form.currency_code || ''} disabled />}
                  </div>
                  <div className="form-row"><label>Hesap No</label><input value={form.account_number || ''} onChange={e => f('account_number', e.target.value)} disabled={!isEditing} placeholder="12345678" /></div>
                  <div className="form-row full"><label>IBAN</label><input value={form.iban || ''} onChange={e => f('iban', e.target.value.toUpperCase())} disabled={!isEditing} placeholder="TR00 0000 0000 0000 0000 0000 00" style={{ fontFamily: 'monospace', letterSpacing: 1 }} /></div>
                  <div className="form-row">
                    <label>Durum</label>
                    <select value={form.is_active ? '1' : '0'} onChange={e => f('is_active', e.target.value === '1')} disabled={!isEditing}>
                      <option value="1">Aktif</option><option value="0">Pasif</option>
                    </select>
                  </div>

                  {isTermAccount && <>
                    <div className="form-row full" style={{ margin: '4px 0 0' }}>
                      <label style={{ color: 'var(--warning)', fontWeight: 700 }}>📅 Vade Bilgileri</label>
                    </div>
                    <div className="form-row"><label>Vade Başlangıçi *</label><input type="date" value={form.term_start_date || ''} onChange={e => f('term_start_date', e.target.value)} disabled={!isEditing} /></div>
                    <div className="form-row"><label>Vade Bitişi *</label><input type="date" value={form.term_end_date || ''} onChange={e => f('term_end_date', e.target.value)} disabled={!isEditing} /></div>
                    <div className="form-row"><label>Yillik Faiz (%)</label><input type="number" step="0.01" min="0" max="100" value={form.interest_rate ?? ''} onChange={e => f('interest_rate', e.target.value)} disabled={!isEditing} placeholder="45.50" /></div>
                    {!isEditing && form.term_end_date && (() => {
                      const days = Math.ceil((new Date(form.term_end_date) - new Date()) / 86400000);
                      return <div className="form-row"><label>Kalan Sure</label><input value={days > 0 ? `${days} gun kaldi` : days === 0 ? 'Bugün vade sonu' : `${Math.abs(days)} gun gecti`} disabled style={{ color: days > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }} /></div>;
                    })()}
                  </>}

                  <div className="form-row full" style={{ margin: '4px 0 0' }}>
                    <label style={{ fontWeight: 700, color: 'var(--text-secondary)', fontSize: '0.72rem', textTransform: 'uppercase' }}>Banka Musteri Temsilcisi</label>
                  </div>
                  <div className="form-row"><label>Ad Soyad</label><input value={form.bank_rep_name || ''} onChange={e => f('bank_rep_name', e.target.value)} disabled={!isEditing} placeholder="Temsilci adi soyadı" /></div>
                  <div className="form-row"><label>Telefon</label><input value={form.bank_rep_phone || ''} onChange={e => f('bank_rep_phone', e.target.value)} disabled={!isEditing} placeholder="0212 xxx xx xx" /></div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="form-grid">
                  <div className="form-row full"><label>Notlar</label><textarea rows="6" value={form.notes || ''} onChange={e => f('notes', e.target.value)} disabled={!isEditing} placeholder="Bu hesapla ilgili notlar..." /></div>
                </div>
              )}

              {activeTab === 'custom' && selected?.id && (
                <CustomFieldsSection entityType="bank_account" entityId={selected.id} autoSave disabled={!isEditing} />
              )}

              {activeTab === 'hareketler' && (
                txLoading ? (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-tertiary)', fontSize: '0.83rem' }}>Yükleniyor...</div>
                ) : transactions.length === 0 ? (
                  <EmptyState title="Hareket yok" description="Henuz islem yok" compact />
                ) : (
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <table className="stocks-table" style={{ fontSize: '0.8rem', minWidth: 720 }}>
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}></th>
                          <th>Tarih</th>
                          <th>Açıklama</th>
                          <th>Tür</th>
                          <th style={{ textAlign: 'right' }}>Tutar</th>
                          <th style={{ textAlign: 'right' }}>Bakiye</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map(tx => {
                          const td = txDisplay(tx.transaction_type);
                          // Banka hareketleri — önce source_type üzerinden kaynak belgeye (tahsilat, çek, POS) yönlendir
                          const openLinkedDoc = () => {
                            if (!onNavigate) return;
                            // 1) source_type tanımlıysa kaynak belgeye git (öncelikli)
                            if (tx.source_type && tx.source_id) {
                              const srcMap = { cari_receipt: 'cari-tahsilat', cari_payment: 'cari-odeme', pos_slip: 'bank-pos-slips' };
                              let route = srcMap[tx.source_type];
                              if (tx.source_type === 'checks_notes') {
                                const d = tx.source_check_doc_type, dir = tx.source_check_direction;
                                if (d === 'check' && dir === 'incoming') route = 'cek-portfoy';
                                else if (d === 'check' && dir === 'outgoing') route = 'cek-verilen';
                                else if (d === 'note'  && dir === 'incoming') route = 'senet-portfoy';
                                else if (d === 'note'  && dir === 'outgoing') route = 'senet-verilen';
                                else route = 'cek-portfoy';
                              }
                              if (route) { setShowModal(false); onNavigate(route, tx.source_id); return; }
                            }
                            // 2) Aksi halde banka hareketi sayfasına git (manuel kayıtlar)
                            const t = tx.transaction_type;
                            const map = {
                              opening: 'bank-acilis', credit: 'bank-alacak', debit: 'bank-borc',
                              virman_in: 'bank-virman', virman_out: 'bank-virman',
                            };
                            const targetPage = map[t];
                            if (targetPage) { setShowModal(false); onNavigate(targetPage, tx.id); }
                            else alert('Bu hareket tipi için yönlendirme yok: ' + t);
                          };
                          // Detay butonu: informational satır olsa bile source_type varsa görünmeli (örn. bekleyen çek)
                          const showDetail = !!tx.source_type || !td.informational;
                          return (
                            <tr key={tx.id}>
                              <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                                {showDetail && (
                                  <button onClick={openLinkedDoc} title="Belgeyi aç"
                                    style={{ background: 'transparent', border: '1px solid var(--border-secondary)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: 'var(--accent-primary)' }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                                  </button>
                                )}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</td>
                              <td>
                                <div>{tx.description}</div>
                                {(tx.transaction_type === 'virman_in' || tx.transaction_type === 'virman_out') && tx.linked_account_name && (
                                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                                    {tx.transaction_type === 'virman_out' ? '→ ' : '← '}{tx.linked_account_name}
                                  </div>
                                )}
                                {tx.counterparty && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{tx.counterparty}</div>}
                              </td>
                              <td>
                                <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, background: td.color + '18', color: td.color }}>
                                  {td.label}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: td.color, whiteSpace: 'nowrap', fontStyle: td.informational ? 'italic' : 'normal' }}>
                                {td.informational ? '' : (td.sign > 0 ? '+' : '-')}{fmt(tx.amount, selected?.currency_code)}
                                {td.informational && <span style={{ fontSize: '0.7rem', marginLeft: 4, color: 'var(--text-tertiary)' }}>(bilgi)</span>}
                              </td>
                              <td style={{ textAlign: 'right', color: td.informational ? 'var(--text-tertiary)' : (parseFloat(tx.balance_after) < 0 ? 'var(--danger)' : 'var(--success)'), whiteSpace: 'nowrap', fontStyle: td.informational ? 'italic' : 'normal' }}>
                                {td.informational ? '—' : fmt(tx.balance_after, selected?.currency_code)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              )}
          </div>
        </Modal>
        );
      })()}

      {showBankManager && <BankManagerPanel onClose={() => { setShowBankManager(false); fetchBankDefs(); }} />}
    </div>
  );
}
